import { useState } from 'react';

interface ResearchOutputsProps {
  outputs: any[];
  isAdmin?: boolean;
  currentUser?: any;
  userProfiles?: Record<string, string>;
  onUpdate?: () => void;
  onEdit?: (item: any) => void;
  onDelete?: (id: string) => void;
}

function UserBadge({ username }: { username?: string }) {
  if (!username) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 text-white rounded-full text-xs font-medium whitespace-nowrap">
      <i className="ri-user-line text-xs"></i>
      {username}
    </span>
  );
}

export default function ResearchOutputs({
  outputs,
  isAdmin = false,
  currentUser,
  userProfiles = {},
  onUpdate,
  onEdit,
  onDelete,
}: ResearchOutputsProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    if (onDelete) {
      onDelete(id);
      setDeleteConfirm(null);
    }
  };

  const canModify = (item: any) => {
    if (isAdmin) return true;
    if (!currentUser) return false;
    return item.user_id === currentUser.id;
  };

  if (!Array.isArray(outputs) || outputs.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-file-download-line text-teal-600 dark:text-teal-400"></i>
          </div>
          成果物
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">成果物がまだ登録されていません。</p>
      </div>
    );
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case '1枚概要PDF':
        return 'ri-file-text-line';
      case 'スライドPDF':
      case 'スライドPPT':
        return 'ri-slideshow-line';
      case '草稿PDF':
        return 'ri-draft-line';
      case '文献リスト':
        return 'ri-list-check-line';
      default:
        return 'ri-file-line';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case '1枚概要PDF':
        return 'bg-teal-50 text-teal-700';
      case 'スライドPDF':
      case 'スライドPPT':
        return 'bg-emerald-50 text-emerald-700';
      case '草稿PDF':
        return 'bg-amber-50 text-amber-700';
      case '文献リスト':
        return 'bg-rose-50 text-rose-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
          <i className="ri-file-download-line text-teal-600 dark:text-teal-400"></i>
        </div>
        成果物
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {outputs.map((output) => {
          const {
            id,
            type = 'その他',
            version = '',
            date = '',
            summary = '',
            change_log,
            file = '#',
          } = output;
          let formattedDate = '';
          try {
            formattedDate = new Date(date).toLocaleDateString('ja-JP');
          } catch {
            formattedDate = '';
          }
          const userCanModify = canModify(output);
          const username = output.user_profiles?.username || null;

          return (
            <div
              key={id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
              <div className="bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-600 px-5 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/15 rounded-lg">
                      <i className={`${getTypeIcon(type)} text-lg`}></i>
                    </div>
                    <div>
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${getTypeColor(
                          type
                        )}`}
                      >
                        {type}
                      </span>
                      <p className="text-xs text-teal-100 mt-1">
                        バージョン {version}
                      </p>
                      {username && (
                        <div className="mt-1">
                          <UserBadge username={username} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-teal-100">{formattedDate}</p>
                    {userCanModify && onEdit && (
                      <button
                        onClick={() => onEdit(output)}
                        className="w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-white/25 rounded-lg transition-colors cursor-pointer"
                        title="編集"
                      >
                        <i className="ri-edit-line text-base"></i>
                      </button>
                    )}
                    {userCanModify && onDelete && (
                      <button
                        onClick={() => setDeleteConfirm(String(id))}
                        className="w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-white/25 rounded-lg transition-colors cursor-pointer"
                        title="削除"
                      >
                        <i className="ri-delete-bin-line text-base"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                    概要
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {summary}
                  </p>
                </div>
                {change_log && (
                  <div className="bg-amber-50/60 dark:bg-amber-900/10 rounded-lg p-3.5">
                    <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <i className="ri-edit-line"></i>変更点
                    </h4>
                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {change_log}
                    </p>
                  </div>
                )}
                <a
                  href={file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white font-medium py-2.5 rounded-lg transition-all text-center whitespace-nowrap text-sm cursor-pointer"
                >
                  <i className="ri-download-line mr-1.5"></i>ダウンロード
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-rose-100 dark:bg-rose-900/40 rounded-full">
                <i className="ri-alert-line text-rose-600 dark:text-rose-400 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                  削除の確認
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  この操作は取り消せません
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
              この成果物を削除してもよろしいですか？
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}